/**
 * Pauzeer (en hervat) de reminder-cadence van een Werk_Order per spoor — voor
 * klanten die op vakantie zijn of pas later op-/afgeleverd willen worden.
 * Proxy naar ReminderSnoozeService (POST /api/orders/snooze-reminders).
 */

import { supabase } from './supabase';

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type SnoozeSpoor = 'aflever' | 'ophaal' | 'beide';

export interface SnoozeResult {
  ok: boolean;
  werkOrderId?: string;
  werkOrderNummer?: string;
  /** Nieuwe pauze-datum 'YYYY-MM-DD' of null (geen pauze). */
  afleverPauzeTot?: string | null;
  ophaalPauzeTot?: string | null;
  message?: string;
  error?: string;
  code?: string;
}

async function post(body: Record<string, unknown>): Promise<SnoozeResult> {
  const headers = await authHeader();
  const res = await fetch('/api/orders/snooze-reminders', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as SnoozeResult;
  return { ...data, ok: res.ok && data.ok !== false };
}

/**
 * Pauzeer een reminderspoor t/m een datum. `tot` weglaten => default +21 dagen
 * (backend), zodat de order na 3 weken vanzelf weer bovendrijft.
 */
export async function snoozeReminders(args: {
  werkOrderId: string;
  spoor: SnoozeSpoor;
  /** 'YYYY-MM-DD'. Weglaten => default +21d. */
  tot?: string;
}): Promise<SnoozeResult> {
  const body: Record<string, unknown> = {
    werkOrderId: args.werkOrderId,
    spoor: args.spoor,
  };
  if (args.tot) body.tot = args.tot;
  return post(body);
}

/**
 * Hervat een reminderspoor nu (wist de pauze-datum). Stuurt `tot: null` —
 * dat onderscheidt zich bewust van "weglaten" (= default +21d).
 */
export async function resumeReminders(args: {
  werkOrderId: string;
  spoor: SnoozeSpoor;
}): Promise<SnoozeResult> {
  return post({ werkOrderId: args.werkOrderId, spoor: args.spoor, tot: null });
}
